import React, { forwardRef, useEffect, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Autocomplete,
  Button,
  Datepicker,
  GridContainer,
  GridItem,
  PercentTextField,
  Select,
  TextField,
} from '../shared';
import { FieldArray, Form, FormikProvider, useFormik } from 'formik';
import { HighlightOff } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { leaseSchema } from '../../schema/validationSchemas';
import {
  getCurrency,
  dateFormatForApi,
  getDateFormat,
  getUniqueSubLocations,
  formatDateForApiSafely,
} from '../../utils/common-methods';
import useStyles from './style';
import dayjs from 'dayjs';
import { MESSAGE } from '../../constants/message';
import { LoadingButton } from '@mui/lab';
import { PerformantDecimalField } from '../shared/PerformantDecimalField/PerformantDecimalField';
import { CURRENCY_CONSTANT } from '../../constants/CurrencyConstants';
import {
  DYNAMICTEXTFIELDS,
  LEASE_COIN_COST_UNIT_OF_MEASURES,
  PRODUCT_CATEGORY,
  RENT_TYPE_CONFIG,
  RENT_TYPES,
} from '../../constants/rentDetailsConfig';
import { useGetAllSublocationsQuery } from '../../services/Sublocation/SublocationService';
import { useDebounce } from '../../hooks';
import { leaseDetailsFormAction } from '../../redux-slice/lease';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  useLazyValidateSubLocationsQuery,
  useAddLeaseMutation,
  useOverlapLeaseMutation,
  useGetRentOptionsQuery,
} from '../../services/Lease/LeaseService';
import ConfirmationModal from '../ConfirmationModel/ConfirmationModal';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { useGetLocationDetailsByIdQuery } from '../../services/Location/LocationService';
import { routeConstants } from '../../constants/routeConstants';
import LeaseHistory from '../../pages/Lease/LeaseHistory/LeaseHistory';
import Modal from '../../components/shared/Modal';
const displayName = 'RentDetailsForm';
const RentDetailsForm = forwardRef((props, ref) => {
  void ref;
  const today = dayjs().toDate();
  const classes = useStyles();
  const { isUpdate = false, isLoading = false, onCancelClick = () => {}, historyActionType = null } = props;
  const leaseState = useSelector((state) => state?.lease?.leaseForms);
  const { rentDetails } = leaseState;
  const [dynamicTextFields, setDynamicTextFields] = useState([]);
  const [subLocations, setSubLocations] = useState([]);
  const [subLocationSearch, setSubLocationSearch] = useState('');
  const [locationName, setLocationName] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [savedFormData, setSavedFormData] = useState(null);
  const didMountRef = useRef(false);

  const [subLocationsModal, setSubLocationsModal] = useState(false);
  const [subLocationNames, setSubLocationsNames] = useState([]);
  const [addLease] = useAddLeaseMutation();
  const debouncedSubLocationSearch = useDebounce(subLocationSearch, 300);
  const dispatch = useDispatch();
  const params = useParams();
  const leaseId = Number(params.id);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const locationId = searchParams.get('location');
  const subLocationId = searchParams.get('subLocation');
  const [overlapLeaseDate] = useOverlapLeaseMutation();
  const [overlappingLeaseDates, setOverlappingLeaseDates] = useState({ termStartDate: '', termEndDate: '' });
  const overrideConfirmedRef = useRef(false);
  const leaseBeginsInputRef = useRef(null);
  const [leaseBeginsError, setLeaseBeginsError] = useState(null);
  const [inEffectFromError, setInEffectFromError] = useState('');
  const [historyModal, setHistoryModal] = useState(false);
  const [validateSubLocationsApi] = useLazyValidateSubLocationsQuery();
  const updatedById = useSelector((state) => state.user?.userDetails?.id);
  const { currentData: rentOptionsList = [] } = useGetRentOptionsQuery();
  const defaultRentOptionAppliedRef = useRef(false);

  const { currentData: sublocationsResponse } = useGetAllSublocationsQuery(
    {
      searchParams:
        isUpdate && selectedLocationId
          ? `locationId=${selectedLocationId}&q=${debouncedSubLocationSearch}`
          : `q=${debouncedSubLocationSearch}`,
      perPage: 100,
    },
    { skip: false },
  );
  const { currentData: locationSublocationsResponse } = useGetLocationDetailsByIdQuery(
    {
      id: selectedLocationId,
      perPage: 100,
    },
    { skip: !selectedLocationId },
  );

  const onSubLocationInputChange = (event, value) => {
    setSubLocationSearch(value);
  };

  useEffect(() => {
    // Only update sublocations if selectedLocationId actually changed
    if (selectedLocationId) {
      setSubLocations(getUniqueSubLocations(locationSublocationsResponse?.data?.sublocations || []));
    } else {
      setSubLocations(getUniqueSubLocations(sublocationsResponse || []));
    }
  }, [selectedLocationId, locationSublocationsResponse, sublocationsResponse]);
  useEffect(() => {
    if (isUpdate && rentDetails?.location?.id && !selectedLocationId) {
      setSelectedLocationId(rentDetails.location.id);
    }
  }, [isUpdate, rentDetails?.location?.id]);
  const onSubLocationChange = (event, selectedSubLocations) => {
    const uniqueSelectedSubLocations = getUniqueSubLocations(selectedSubLocations);
    setFieldValue('subLocation', uniqueSelectedSubLocations);

    if (uniqueSelectedSubLocations?.length > 0) {
      const firstValid = uniqueSelectedSubLocations.find((sub) => sub?.location?.id);

      if (!firstValid) return;

      const validLocationSubLoc = uniqueSelectedSubLocations.find((sub) => sub?.location);

      if (validLocationSubLoc?.location) {
        const { location } = validLocationSubLoc;
        const updatedLocation = {
          id: location.id,
          name: location.locationName,
          address1: location.address1 || '',
          address2: location.address2 || '',
          zipCode: location.postcode || '',
          country: location.countryId || '',
          state: location.stateId || '',
          city: location.cityId || '',
        };

        setLocationName(location.locationName || '');
        setSelectedLocationId(location.id);

        setFieldValue('location', updatedLocation);
        setFieldValue('address1', updatedLocation.address1);
        setFieldValue('address2', updatedLocation.address2);
        setFieldValue('zipCode', updatedLocation.zipCode);
        setFieldValue('country', updatedLocation.country);
        setFieldValue('state', updatedLocation.state);
        setFieldValue('city', updatedLocation.city);

        const productTypes = uniqueSelectedSubLocations.flatMap(
          (subLoc) => subLoc?.productCategories?.map((pc) => pc.code) || [],
        );
        const uniqueProductTypes = [...new Set(productTypes)];
        setFieldValue('productTypes', uniqueProductTypes);

        const newLeaseName = generateLeaseName(updatedLocation, uniqueProductTypes, values.leaseBegins);
        setFieldValue('leaseName', newLeaseName);

        dispatch(
          leaseDetailsFormAction({
            ...values,
            location: updatedLocation,
            subLocation: uniqueSelectedSubLocations,
            address1: updatedLocation?.address1,
            address2: updatedLocation?.address2,
            zipCode: updatedLocation?.zipCode,
            country: updatedLocation?.country,
            state: updatedLocation?.state,
            city: updatedLocation?.city,
            productTypes: uniqueProductTypes,
            leaseName: isUpdate ? values.leaseName : newLeaseName,
          }),
        );
      }
    } else {
      setSelectedLocationId(null);
      setLocationName('');
      setFieldValue('location', null);
      setFieldValue('address1', '');
      setFieldValue('leaseName', '');
      setFieldValue('productTypes', []);
    }

    if (isUpdate) {
      setSubLocationSearch('');
    }
    const dynamicFields = checkDynamicTextFieldsIsPresent(uniqueSelectedSubLocations);
    setDynamicTextFields(dynamicFields);
  };

  // Function to check and set dynamicTextFieldsValues
  function checkDynamicTextFieldsIsPresent(subLocations) {
    let dynamicTextFieldsValues = [];
    if (subLocations && subLocations?.length) {
      for (const subLocation of subLocations) {
        if (subLocation?.productCategories && subLocation?.productCategories?.length) {
          const hasGameOrArcade = subLocation.productCategories.some(
            (category) =>
              category.x3_productname === PRODUCT_CATEGORY.GAME.name ||
              category.x3_productname === PRODUCT_CATEGORY.ARCADE.name ||
              category.id === PRODUCT_CATEGORY.GAME.id ||
              category.id === PRODUCT_CATEGORY.ARCADE.id,
          );

          if (hasGameOrArcade) {
            dynamicTextFieldsValues = [...DYNAMICTEXTFIELDS];
            return dynamicTextFieldsValues;
          }
        }
      }
    }

    return dynamicTextFieldsValues;
  }

  const customeValidation = (values, setFieldError) => {
    let isValid = true;
    if (
      values.rentType === 'RATE_ONLY_TIERED' ||
      values.rentType === 'RATE_ONLY_TIERED_TERM_COMBO' ||
      values.rentType === 'FEE_PLUS_TIERED_RATE'
    ) {
      const { tierSummaryState } = values;
      for (let i = 1; i < tierSummaryState.length; i++) {
        const current = parseFloat(tierSummaryState[i]?.tierThreshold) || 0;
        const previous = parseFloat(tierSummaryState[i - 1]?.tierThreshold) || 0;
        if (current <= previous) {
          setFieldError(`tierSummaryState[${i}].tierThreshold`, `Tier Threshold must be greater than ${previous}`);
          isValid = false;
        }
      }
    }
    return isValid;
  };

  const generateLeaseName = (location, productTypeCodes, leaseBegins) => {
    if ((!location || !location.name) && !leaseBegins) return '';

    const locationPart = location?.name || '';

    const date = leaseBegins ? new Date(leaseBegins) : new Date();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    const datePart = leaseBegins ? ` - ${month} ${year}` : '';

    const uniqueCodes = productTypeCodes ? [...new Set(productTypeCodes)] : [];
    const productCodes = uniqueCodes.length > 0 ? ` - ${uniqueCodes.join('-')}` : '';

    return `${locationPart}${productCodes}${datePart}`.replace(/\s+/g, ' ').trim();
  };
  const filteredSubLocationsWithOverlappingSubLocations = (data, values) => {
    const overlapSubLocations = data?.filter((value) => value.overlap);
    const filteredSubLocations = values?.subLocation.filter((el) =>
      overlapSubLocations.some((f) => f.sublocationId == el.id),
    );
    return filteredSubLocations;
  };

  const formik = useFormik({
    initialValues: rentDetails,
    validationSchema: leaseSchema.rentDetailsSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setFieldError, setSubmitting }) => {
      setSavedFormData(JSON.parse(JSON.stringify(values)));
      const subLocationIds = values?.subLocation?.map((value) => value.id).join(',') || [];

      if (!customeValidation(values, setFieldError)) {
        setSubmitting(false);
        return;
      }

      const updatedValues = { ...values };
      if (!RENT_TYPE_CONFIG.dynamicTextFields.includes(updatedValues.rentType)) {
        updatedValues.modalTypes = [];
      }

      try {
        // First validate sublocations unless override was already confirmed.
        if (!overrideConfirmedRef.current) {
          let apiData = {
            locationId: updatedValues.location?.id || null,
            termStartDate: updatedValues.leaseBegins ? dateFormatForApi(new Date(updatedValues.leaseBegins)) : null,
            termEndDate: updatedValues.leaseEnds ? dateFormatForApi(new Date(updatedValues.leaseEnds)) : null,
            subLocationIds: subLocationIds,
          };
          if (isUpdate) {
            apiData = { ...apiData, contractId: leaseId };
          }

          const validationResponse = await validateSubLocationsApi(apiData);

          if (validationResponse?.data.length) {
            if (validationResponse.data.length > 0) {
              const { termStartDate, termEndDate } = validationResponse.data[0];
              setOverlappingLeaseDates({ termStartDate, termEndDate });
            }
            const filteredSubLocations = filteredSubLocationsWithOverlappingSubLocations(
              validationResponse.data,
              values,
            );
            if (filteredSubLocations?.length > 0) {
              const subLocationNames = filteredSubLocations.map((value) => value.name);
              setSubLocationsNames(subLocationNames);
              setSubLocationsModal(true);
              setSubmitting(false);
              return;
            }
          }
        }

        const gameCategoryRates = updatedValues?.modalTypes?.map((value) => ({
          gameCategoryId: value?.id,
          rate: value?.rate,
        }));

        // Prepare the lease data
        const leaseData = {
          name: updatedValues.leaseName || '',
          locationId: updatedValues.location?.id || null,
          sublocationIds: updatedValues.subLocation?.map((subLoc) => subLoc.id) || [],
          rentOptionIds: updatedValues.rentOptions?.map((rentOption) => rentOption.id) || [],
          gameCategoryRates: gameCategoryRates || [],
          modalTypes: RENT_TYPE_CONFIG.dynamicTextFields.includes(updatedValues.rentType)
            ? updatedValues.modalTypes
            : [],
          rentType: updatedValues.rentType || null,
          termStartDate: updatedValues.leaseBegins ? dateFormatForApi(new Date(updatedValues.leaseBegins)) : null,
          termEndDate: updatedValues.leaseEnds ? dateFormatForApi(new Date(updatedValues.leaseEnds)) : null,
          fee: RENT_TYPE_CONFIG.rentFee.includes(updatedValues.rentType) ? updatedValues.rentFee : null,
          rate: RENT_TYPE_CONFIG.rentRate.includes(updatedValues.rentType) ? updatedValues.rentRate : null,
          CAMFee: RENT_TYPE_CONFIG.CAMFee.includes(updatedValues.rentType) ? updatedValues.CAMFee : null,
          CAMRate: RENT_TYPE_CONFIG.CAMRate.includes(updatedValues.rentType) ? updatedValues.CAMRate : null,
          breakpointRevenue: RENT_TYPE_CONFIG.breakpointRevenue.includes(updatedValues.rentType)
            ? updatedValues.breakpointRevenue
            : null,
          breakpointRate: RENT_TYPE_CONFIG.breakpointRate.includes(updatedValues.rentType)
            ? updatedValues.breakpointRate
            : null,
          resetTerm: RENT_TYPE_CONFIG.resetTerm.includes(updatedValues.rentType) ? updatedValues.resetTerm : null,
          payFrequency: RENT_TYPE_CONFIG.payFrequency.includes(updatedValues.rentType)
            ? updatedValues.payFrequency
            : null,
          coinCost: updatedValues.coinCost ?? null,
          coinCostUom: updatedValues.coinCostUom || LEASE_COIN_COST_UNIT_OF_MEASURES.DOLLAR,
          // null when unset so the backend's column default applies instead of hardcoding it here.
          creditCardFeeRate:
            updatedValues.creditCardFeeRate === '' || updatedValues.creditCardFeeRate === undefined
              ? null
              : updatedValues.creditCardFeeRate,
          cardCost: updatedValues.cardCost ?? null,
          notes: updatedValues.notes || '',
          contractLink: updatedValues.contractLink || '',
          tierBreakpointAndRate:
            (RENT_TYPE_CONFIG.tierBreakpointAndRate.includes(updatedValues.rentType) &&
              updatedValues?.tierSummaryState
                ?.filter((item) => item?.tierThreshold > 0 && item?.tierRate >= 0)
                .map((item) => ({
                  tierThreshold: item.tierThreshold,
                  tierRate: item.tierRate,
                }))) ||
            [],
          inEffectFrom: formatDateForApiSafely(updatedValues.inEffectFrom),
          inEffectTo: formatDateForApiSafely(updatedValues.inEffectTo),
          override: overrideConfirmedRef.current,
          ...(isUpdate && { updatedBy: updatedById }),
          updatedBy: updatedById,
        };

        // Call the appropriate mutation based on isUpdate
        if (isUpdate) {
          await props.onUpdateClick(leaseData);
        } else {
          // Use the addLease mutation
          await addLease(leaseData).unwrap();

          if (locationId && subLocationId) {
            navigate(`/${routeConstants.SUBLOCATION_ROUTE}/${subLocationId}?locationId=${locationId}`, {
              replace: true,
            });
          } else {
            navigate(`/${routeConstants.LEASE_ROUTE}?selectedTab=1`, {
              replace: true,
            });
          }
        }
      } catch (error) {
        setFieldError('apiError', error.message || 'Something went wrong while saving lease details.');
      } finally {
        overrideConfirmedRef.current = false;
        setSubmitting(false);
      }
    },
  });
  // added async await to handle the promise rejection
  const handleModalClick = async (value) => {
    if (value === MESSAGE.YES) {
      setSubLocationsModal(false);
      const subLocationIds = values?.subLocation?.map((value) => Number(value.id)) || [];
      if (subLocationIds?.length > 0) {
        let data = {
          locationId: values?.location?.id,
          subLocationIds: subLocationIds,
          termStartDate: dateFormatForApi(values?.leaseBegins),
          termEndDate: dateFormatForApi(values?.leaseEnds),
        };
        try {
          await overlapLeaseDate(data).unwrap();
          overrideConfirmedRef.current = true;
          handleSubmit();
        } catch (error) {
          overrideConfirmedRef.current = false;
          console.error('Error handling lease overlap:', error);
        }
      }
    } else {
      setSubLocationsModal(false);
      if (savedFormData) {
        didMountRef.current = true;
        resetForm({ values: savedFormData });
        dispatch(leaseDetailsFormAction(savedFormData));
      }

      const errorMessage = overlappingLeaseDates?.termEndDate
        ? `The lease begins date must be ${getDateFormat(
            dayjs(overlappingLeaseDates?.termEndDate).add(1, 'day').toDate(),
          )} or greater`
        : 'The lease begins date must be greater';
      setLeaseBeginsError(errorMessage);
      if (leaseBeginsInputRef.current) {
        leaseBeginsInputRef.current.focus();
      }
    }
  };
  const {
    errors,
    values,
    touched,
    getFieldProps,
    handleSubmit,
    setFieldValue,
    setValues,
    handleBlur,
    isSubmitting,
    resetForm,
  } = formik;

  useEffect(() => {
    if (historyActionType === 'add') {
      setFieldValue('inEffectFrom', null);
      setFieldValue('inEffectTo', null);
    }
  }, [historyActionType, setFieldValue]);

  useEffect(() => {
    if (values.location && values.location.name) {
      const newLeaseName = generateLeaseName(values.location, values.productTypes, values.leaseBegins);

      // Only update if we're not in edit mode or if the name would actually change
      if (!isUpdate || values.leaseName !== newLeaseName) {
        setFieldValue('leaseName', newLeaseName);
      }

      dispatch(
        leaseDetailsFormAction({
          ...values,
          leaseName: isUpdate ? values.leaseName : newLeaseName, // Preserve existing name in edit mode
          leaseBegins: values.leaseBegins,
        }),
      );
    }
  }, [values.location, values.productTypes, values.leaseBegins]);

  useEffect(() => {
    if (!values.subLocation || values.subLocation.length === 0) {
      setLeaseBeginsError('');
    }
    if (values.subLocation && values.subLocation.length > 0) {
      // Recalculate product types whenever sublocations change
      const productTypes = values.subLocation.flatMap(
        (subLoc) => subLoc?.productCategories?.map((pc) => pc.code) || [],
      );
      const uniqueProductTypes = [...new Set(productTypes)];

      if (JSON.stringify(uniqueProductTypes) !== JSON.stringify(values.productTypes)) {
        setFieldValue('productTypes', uniqueProductTypes);

        const newLeaseName = generateLeaseName(values.location, uniqueProductTypes, values.leaseBegins);
        setFieldValue('leaseName', newLeaseName);
      }
    }
  }, [values.subLocation]);
  useEffect(() => {
    if (!didMountRef.current) {
      setValues(rentDetails);
      let dynamicFields;

      if (rentDetails?.modalTypes && rentDetails?.modalTypes.length) {
        dynamicFields = rentDetails.modalTypes;
        if (dynamicFields.length < 3) {
          const predefinedFields = [...DYNAMICTEXTFIELDS];
          const missingFields = predefinedFields.filter(
            (predefinedField) => !dynamicFields.some((field) => field.id === predefinedField.id),
          );
          dynamicFields = [...dynamicFields, ...missingFields];
        }
      } else {
        dynamicFields = checkDynamicTextFieldsIsPresent();
      }

      setDynamicTextFields(dynamicFields);

      // Set initial location name if sublocations exist
      if (rentDetails?.subLocation?.length > 0) {
        setLocationName(rentDetails.subLocation[0]?.location?.locationName || '');
      }
      didMountRef.current = true;
    }
  }, [rentDetails]);

  useEffect(() => {
    if (
      !isUpdate &&
      !defaultRentOptionAppliedRef.current &&
      rentOptionsList.length > 0 &&
      (!values.rentOptions || values.rentOptions.length === 0)
    ) {
      const defaultRentOption = rentOptionsList.find((option) => option.name === 'CC on Net Sales');
      if (defaultRentOption) {
        setFieldValue('rentOptions', [defaultRentOption]);
      }
      defaultRentOptionAppliedRef.current = true;
    }
  }, [isUpdate, rentOptionsList]);

  const dynamicTextFieldChangeHandler = (currentIndex, value) => {
    const modalTypeArr = [...dynamicTextFields];
    const currentObj = modalTypeArr[currentIndex];
    const copyObj = { ...currentObj };
    copyObj.rate = value;
    modalTypeArr[currentIndex] = copyObj;
    setDynamicTextFields(modalTypeArr);
    setFieldValue('modalTypes', modalTypeArr);
  };

  useEffect(() => {
    const visibleFields = getVisibleMandatoryFields(values.rentType);
    const resetTouchedFields = visibleFields.reduce((acc, field) => {
      acc[field] = false;
      return acc;
    }, {});
    formik.setTouched(resetTouchedFields);
  }, [values.rentType]);

  const rentTypeFieldMap = {
    'Fee Only': ['rentFee'],
    'Rate Only': ['rentRate'],
    'Fee Plus Rate': ['rentFee', 'rentRate'],
    'Fee Plus Breakpoint Rate - Monthly': ['rentFee', 'breakpointRevenue', 'breakpointRate'],
    'Fee Plus Breakpoint Rate - Term': ['rentFee', 'resetTerm', 'payFrequency', 'breakpointRevenue', 'breakpointRate'],
    'Fee Plus Combo Rate - Monthly': ['rentFee', 'breakpointRevenue', 'breakpointRate'],
    'Fee Plus Combo Rate - Term': ['rentFee', 'resetTerm', 'payFrequency', 'breakpointRevenue', 'breakpointRate'],
    'Rate Only Tiered': ['rentRate', 'resetTerm', 'payFrequency'],
    'Rate Only Tiered - Term Combo': ['rentRate', 'resetTerm', 'payFrequency'],
    'Fee Plus Tiered Rate': ['rentFee', 'resetTerm', 'payFrequency', 'breakpointRevenue'],
    'Rate Plus Breakpoint Rate - Term': ['rentRate', 'resetTerm', 'payFrequency', 'breakpointRate'],
  };

  const getVisibleMandatoryFields = (rentType) => rentTypeFieldMap[rentType] || [];

  // const handleHistoryClick = () => {
  //   setHistoryModal(true);
  // };

  const handleCloseHistoryModal = () => {
    setHistoryModal(false);
  };
  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" onSubmit={handleSubmit}>
        <Box className={classes.rentDetailsForm} data-testid="rentDetailComponent">
          <GridContainer rowSpacing={3} columnSpacing={{ xs: 0, sm: 0, md: 6 }}>
            <GridItem xs={12} md={12} pl={0} my={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h2">Lease Details</Typography>
                {/* {isUpdate && <Button onClick={handleHistoryClick}>History</Button>} */}
              </Box>
            </GridItem>

            <GridItem xs={12} pl={0}>
              <Autocomplete
                label="Sub Location"
                value={values.subLocation}
                options={subLocations}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                onChange={onSubLocationChange}
                multiple
                disableCloseOnSelect
                onInputChange={onSubLocationInputChange}
                filterSelectedOptions
                noOptionsText={
                  selectedLocationId || (isUpdate && rentDetails?.location?.id)
                    ? 'No sublocations found for this location'
                    : 'No sublocations found'
                }
                error={Boolean(touched.subLocation && errors.subLocation)}
                helperText={touched.subLocation && (errors.subLocation?.name ?? errors.subLocation)}
              />
            </GridItem>
            <GridItem xs={12} md={4} pl={0}>
              <Datepicker
                label="Lease Begins"
                name="leaseBegins"
                value={values.leaseBegins}
                inputFormat="MM/DD/YYYY"
                InputProps={{
                  'data-testid': 'leaseDetailsForm',
                }}
                onChange={(date) => {
                  const dateStr = date === null ? date : date.toString();
                  setFieldValue('leaseBegins', dateStr);
                  // Check if the new date is valid
                  if (overlappingLeaseDates?.termEndDate) {
                    const minAllowedDate = dayjs(overlappingLeaseDates.termEndDate);
                    const selectedDate = dayjs(dateStr);
                    if (selectedDate.isBefore(minAllowedDate)) {
                      setLeaseBeginsError(
                        `The lease begins date must be ${getDateFormat(
                          minAllowedDate.add(1, 'day').toDate(),
                        )} or greater`,
                      );
                    } else {
                      setLeaseBeginsError('');
                    }
                  } else {
                    setLeaseBeginsError('');
                  }
                  // Update lease name when lease begins date changes
                  if (values.location && values.productTypes) {
                    const newLeaseName = generateLeaseName(values.location, values.productTypes, dateStr);
                    setFieldValue('leaseName', newLeaseName);
                  }
                }}
                error={Boolean(!!leaseBeginsError || (touched.leaseBegins && errors.leaseBegins))}
                helperText={leaseBeginsError || (touched.leaseBegins && errors.leaseBegins)}
                inputRef={leaseBeginsInputRef}
              />
            </GridItem>
            <GridItem xs={12} md={4} pl={0}>
              <Datepicker
                label="Lease Ends"
                name="leaseEnds"
                value={values.leaseEnds}
                inputFormat="MM/DD/YYYY"
                InputProps={{
                  'data-testid': 'leaseDetailsForm',
                }}
                onChange={(date) =>
                  date === null ? setFieldValue('leaseEnds', date) : setFieldValue('leaseEnds', date.toString())
                }
                error={Boolean(touched.leaseEnds && errors.leaseEnds)}
                helperText={touched.leaseEnds && errors.leaseEnds}
              />
            </GridItem>
            <GridItem xs={12} md={4} pl={0}>
              <TextField
                label="Lease Name"
                {...getFieldProps('leaseName')}
                InputProps={{
                  'data-testid': 'leaseDetailsForm',
                }}
                error={Boolean(touched.leaseName && errors.leaseName)}
                helperText={touched.leaseName && errors.leaseName}
              />
            </GridItem>
            <GridItem xs={12} md={4} pl={0}>
              <Datepicker
                label="In Effect From"
                name="inEffectFrom"
                id="inEffectFrom"
                inputFormat="MM/DD/YYYY"
                value={values.inEffectFrom || ''}
                minDate={
                  historyActionType === 'add'
                    ? undefined
                    : isUpdate && props.maxInEffectFromDate
                    ? dayjs(props.maxInEffectFromDate)
                    : today
                }
                onChange={(date) => {
                  const dateStr = date === null ? null : dayjs(date).format('YYYY-MM-DD');
                  setFieldValue('inEffectFrom', dateStr);
                  setInEffectFromError('');
                }}
                disabled={historyActionType === 'edit'}
                error={Boolean(inEffectFromError) || Boolean(touched.inEffectFrom && errors.inEffectFrom)}
                helperText={inEffectFromError || (touched.inEffectFrom && errors.inEffectFrom)}
              />
            </GridItem>
            <GridItem xs={12} md={4} pl={0}>
              <Datepicker
                label="In Effect To"
                name="inEffectTo"
                id="inEffectTo"
                inputFormat="MM/DD/YYYY"
                value={values.inEffectTo || ''}
                onChange={(date) => {
                  const dateStr = date === null ? null : dayjs(date).format('YYYY-MM-DD');
                  setFieldValue('inEffectTo', dateStr);
                }}
                disabled={historyActionType !== 'add'}
                error={Boolean(touched.inEffectTo && errors.inEffectTo)}
                helperText={touched.inEffectTo && errors.inEffectTo}
              />
            </GridItem>
            <GridItem xs={12} md={4} pl={0}>
              <TextField
                label="Contract Link"
                type="text"
                maxRows={4}
                {...getFieldProps('contractLink')}
                InputProps={{
                  'data-testid': 'rentDetailsForm',
                  resize: 'vertical',
                  overflow: 'auto',
                }}
                multiline
                error={Boolean(touched.contractLink && errors.contractLink)}
                helperText={touched.contractLink && errors.contractLink}
              />
            </GridItem>
            <GridItem xs={12} md={4} pl={0}>
              <TextField
                label="Notes"
                type="text"
                {...getFieldProps('notes')}
                InputProps={{
                  'data-testid': 'rentDetailsForm',
                }}
                multiline
                error={Boolean(touched.notes && errors.notes)}
                helperText={touched.notes && errors.notes}
              />
            </GridItem>
            <Grid pl={2} my={2} container spacing={2} justifyContent="flex-end">
              {[
                { label: 'Location :', value: locationName || values.location?.name || 'N/A' },
                { label: 'DBA :', value: values.dba || 'N/A' },
                {
                  label: 'Address :',
                  value: (() => {
                    const fullLocationData = locationSublocationsResponse?.data;

                    const countryName = fullLocationData?.country?.name || values.location?.country;
                    const stateName = fullLocationData?.state?.name || values.location?.state;
                    const cityName = fullLocationData?.city?.name || values.location?.city;

                    const addressLine = values.location?.address1;
                    const locationLine = [
                      cityName,
                      stateName,
                      values.location?.postcode || values.location?.zipCode,
                      countryName,
                    ]
                      .filter(Boolean)
                      .join(', ');

                    return addressLine || locationLine ? (
                      <>
                        {addressLine && (
                          <>
                            {addressLine}
                            <br />
                          </>
                        )}
                        {locationLine && <>{locationLine}</>}
                      </>
                    ) : (
                      'N/A'
                    );
                  })(),
                },
                { label: 'Booth Info :', value: values.boothInfo || 'N/A' },
                {
                  label: 'Space Identifier :',
                  value: (() => {
                    // Get the selected sublocation IDs from form values
                    const selectedSubLocationIds = values.subLocation?.map((sub) => sub.id) || [];

                    // Find matching sublocations in the response data
                    const matchedSubLocations =
                      locationSublocationsResponse?.data?.sublocations?.filter((sub) =>
                        selectedSubLocationIds.includes(sub.id),
                      ) || [];

                    // Extract all space identifiers from matched sublocations
                    const spaceIdentifiers = matchedSubLocations.map((sub) => sub.spaceIdentifier).filter(Boolean);

                    // Join multiple identifiers with comma if needed
                    return spaceIdentifiers.length > 0 ? spaceIdentifiers.join(', ') : 'N/A';
                  })(),
                },
                { label: '', value: '' },
              ].map((item, index) => (
                <GridItem key={index} xs={12} md={6} pl={1} my={-1}>
                  <Box display="flex">
                    <Typography fontWeight="bold" sx={{ minWidth: '133px' }}>
                      {item.label}
                    </Typography>
                    <Typography>{item.value}</Typography>
                  </Box>
                </GridItem>
              ))}
            </Grid>

            <GridItem xs={12} md={12} pl={0} my={2}>
              <Box>
                <Typography variant="h2">Payment Terms</Typography>
              </Box>
            </GridItem>
            <GridItem xs={12} md={4} pl={0} order={1}>
              <Select
                inputLabel="Rent Type"
                options={RENT_TYPES}
                {...getFieldProps('rentType')}
                data-testid="rentDetailsForm"
                error={Boolean(touched.rentType && errors.rentType)}
                helperText={touched.rentType && errors.rentType}
              />
            </GridItem>
            <GridItem xs={12} md={4} pl={0} order={dynamicTextFields?.length > 0 ? 19 + dynamicTextFields?.length : 19}>
              <Autocomplete
                label="Rent Options"
                value={values.rentOptions || []}
                options={rentOptionsList}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                onChange={(event, selected) => setFieldValue('rentOptions', selected || [])}
                multiple
                disableCloseOnSelect
                filterSelectedOptions
                error={Boolean(touched.rentOptions && errors.rentOptions)}
                helperText={touched.rentOptions && errors.rentOptions}
              />
            </GridItem>
            {RENT_TYPE_CONFIG.rentFee.includes(values.rentType) && (
              <GridItem xs={12} md={4} pl={0} order={2}>
                <PerformantDecimalField
                  useFormik={true}
                  priceIcon={true}
                  label="Rent Fee"
                  {...getFieldProps('rentFee')}
                  error={Boolean(touched.rentFee && errors.rentFee)}
                  helperText={touched.rentFee && errors.rentFee}
                />
              </GridItem>
            )}
            {RENT_TYPE_CONFIG.rentRate.includes(values.rentType) && (
              <GridItem xs={12} md={4} pl={0} order={3}>
                <PercentTextField
                  useFormik={true}
                  allowDecimal={true}
                  allowIntermediateDecimal={true}
                  label="Rent Rate"
                  {...getFieldProps('rentRate')}
                  InputProps={{
                    'data-testid': 'rentDetailsForm',
                  }}
                  error={Boolean(touched.rentRate && errors.rentRate)}
                  helperText={touched.rentRate && errors.rentRate}
                />
              </GridItem>
            )}
            {RENT_TYPE_CONFIG.CAMFee.includes(values.rentType) && (
              <GridItem xs={12} md={4} pl={0} order={4}>
                <PerformantDecimalField
                  useFormik={true}
                  priceIcon={true}
                  label="CAM Fee"
                  {...getFieldProps('CAMFee')}
                  error={Boolean(touched.CAMFee && errors.CAMFee)}
                  helperText={touched.CAMFee && errors.CAMFee}
                />
              </GridItem>
            )}
            {RENT_TYPE_CONFIG.CAMRate.includes(values.rentType) && (
              <GridItem xs={12} md={4} pl={0} order={5}>
                <PercentTextField
                  label="CAM Rate"
                  useFormik={true}
                  allowDecimal={true}
                  allowIntermediateDecimal={true}
                  {...getFieldProps('CAMRate')}
                  InputProps={{
                    'data-testid': 'rentDetailsForm',
                  }}
                  error={Boolean(touched.CAMRate && errors.CAMRate)}
                  helperText={touched.CAMRate && errors.CAMRate}
                />
              </GridItem>
            )}
            {RENT_TYPE_CONFIG.dynamicTextFields.includes(values.rentType) && (
              <>
                {dynamicTextFields.length > 0 &&
                  dynamicTextFields.map((value, index) => {
                    const order = index + 1 + 11;
                    return (
                      <GridItem key={order} order={order} xs={12} md={4} pl={0}>
                        <TextField
                          type="number"
                          label={value.name}
                          value={value.rate}
                          InputProps={{
                            'data-testid': 'rentDetailsForm',
                          }}
                          onChange={(e) => {
                            dynamicTextFieldChangeHandler(index, e.target.value);
                          }}
                          error={
                            touched.modalTypes &&
                            errors.modalTypes &&
                            errors.modalTypes[index] &&
                            touched.modalTypes[index] &&
                            Boolean(touched.modalTypes[index]?.rate && errors.modalTypes[index]?.rate)
                          }
                          helperText={
                            touched.modalTypes &&
                            errors.modalTypes &&
                            errors.modalTypes[index] &&
                            touched.modalTypes[index] &&
                            touched.modalTypes[index]?.rate &&
                            errors.modalTypes[index]?.rate
                          }
                        />
                      </GridItem>
                    );
                  })}
              </>
            )}
            {RENT_TYPE_CONFIG.breakpointRevenue.includes(values.rentType) && (
              <GridItem
                xs={12}
                md={4}
                pl={0}
                order={dynamicTextFields?.length > 0 ? 12 + dynamicTextFields?.length : 12}
              >
                <PerformantDecimalField
                  useFormik={true}
                  priceIcon={true}
                  label="Breakpoint Revenue"
                  {...getFieldProps('breakpointRevenue')}
                  decimalPlaces={4}
                  error={Boolean(touched.breakpointRevenue && errors.breakpointRevenue)}
                  helperText={touched.breakpointRevenue && errors.breakpointRevenue}
                />
              </GridItem>
            )}
            {RENT_TYPE_CONFIG.breakpointRate.includes(values.rentType) && (
              <GridItem
                xs={12}
                md={4}
                pl={0}
                order={dynamicTextFields?.length > 0 ? 13 + dynamicTextFields?.length : 13}
              >
                <PercentTextField
                  useFormik={true}
                  allowDecimal={true}
                  allowIntermediateDecimal={true}
                  label="Breakpoint Rate"
                  {...getFieldProps('breakpointRate')}
                  onBlur={(e) => {
                    handleBlur(e);
                  }}
                  InputProps={{
                    'data-testid': 'rentDetailsForm',
                  }}
                  error={Boolean(touched.breakpointRate && errors.breakpointRate)}
                  helperText={touched.breakpointRate && errors.breakpointRate}
                />
              </GridItem>
            )}
            {RENT_TYPE_CONFIG.resetTerm.includes(values.rentType) && (
              <GridItem
                xs={12}
                md={4}
                pl={0}
                order={dynamicTextFields?.length > 0 ? 14 + dynamicTextFields?.length : 14}
              >
                <TextField
                  label="Reset Term"
                  type="number"
                  {...getFieldProps('resetTerm')}
                  InputProps={{
                    'data-testid': 'rentDetailsForm',
                  }}
                  restrictNegativeValue={true}
                  error={Boolean(touched?.resetTerm && errors?.resetTerm)}
                  helperText={touched?.resetTerm && errors?.resetTerm}
                />
              </GridItem>
            )}
            {RENT_TYPE_CONFIG.payFrequency.includes(values.rentType) && (
              <GridItem
                xs={12}
                md={4}
                pl={0}
                order={dynamicTextFields?.length > 0 ? 15 + dynamicTextFields?.length : 15}
              >
                <TextField
                  label="Pay Frequency"
                  type="number"
                  {...getFieldProps('payFrequency')}
                  InputProps={{
                    'data-testid': 'rentDetailsForm',
                  }}
                  restrictNegativeValue={true}
                  error={Boolean(touched.payFrequency && errors?.payFrequency)}
                  helperText={touched?.payFrequency && errors?.payFrequency}
                />
              </GridItem>
            )}
            <GridItem xs={12} md={4} pl={0} order={dynamicTextFields?.length > 0 ? 16 + dynamicTextFields?.length : 16}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box flexGrow={1} minWidth={0}>
                  <PerformantDecimalField
                    useFormik={true}
                    symbol={values.coinCostUom === LEASE_COIN_COST_UNIT_OF_MEASURES.PERCENTAGE ? '%' : '$'}
                    symbolPosition={
                      values.coinCostUom === LEASE_COIN_COST_UNIT_OF_MEASURES.PERCENTAGE ? 'end' : 'start'
                    }
                    label="Coin Cost/Rate"
                    {...getFieldProps('coinCost')}
                    error={Boolean(touched.coinCost && errors.coinCost)}
                    helperText={touched.coinCost && errors.coinCost}
                  />
                </Box>
                <Box flexShrink={0} sx={{ position: 'relative' }}>
                  <Typography
                    component="label"
                    sx={{
                      position: 'absolute',
                      top: -9,
                      left: 10,
                      px: 0.5,
                      zIndex: 1,
                      fontSize: '0.75rem',
                      lineHeight: 1,
                      color: 'text.secondary',
                      backgroundColor: 'background.paper',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Unit of Measure
                  </Typography>
                  <Box
                    sx={{
                      height: 56,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                      borderRadius: '4px',
                      px: 0.5,
                    }}
                  >
                    <ToggleButtonGroup
                      exclusive
                      size="medium"
                      color="primary"
                      data-testid="coinCostUomToggle"
                      value={values.coinCostUom || LEASE_COIN_COST_UNIT_OF_MEASURES.DOLLAR}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setFieldValue('coinCostUom', newValue);
                        }
                      }}
                    >
                      <ToggleButton value={LEASE_COIN_COST_UNIT_OF_MEASURES.DOLLAR} aria-label="Dollar" sx={{ px: 2 }}>
                        $
                      </ToggleButton>
                      <ToggleButton
                        value={LEASE_COIN_COST_UNIT_OF_MEASURES.PERCENTAGE}
                        aria-label="Percentage"
                        sx={{ px: 2 }}
                      >
                        %
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>
              </Box>
            </GridItem>
            <GridItem xs={12} md={4} pl={0} order={dynamicTextFields?.length > 0 ? 17 + dynamicTextFields?.length : 17}>
              <PercentTextField
                useFormik={true}
                allowDecimal={true}
                decimalPlaces={2}
                label="CC default rate"
                {...getFieldProps('creditCardFeeRate')}
                error={Boolean(touched.creditCardFeeRate && errors.creditCardFeeRate)}
                helperText={touched.creditCardFeeRate && errors.creditCardFeeRate}
              />
            </GridItem>
            <GridItem xs={12} md={4} pl={0} order={dynamicTextFields?.length > 0 ? 18 + dynamicTextFields?.length : 18}>
              <PerformantDecimalField
                useFormik={true}
                priceIcon={true}
                label="Card Cost"
                {...getFieldProps('cardCost')}
                error={Boolean(touched.cardCost && errors?.cardCost)}
                helperText={touched?.cardCost && errors?.cardCost}
              />
            </GridItem>
          </GridContainer>

          {(values.rentType === 'RATE_ONLY_TIERED' ||
            values.rentType === 'RATE_ONLY_TIERED_TERM_COMBO' ||
            values.rentType === 'FEE_PLUS_TIERED_RATE') && (
            <Box>
              <GridContainer my={2} className={classes.accountSummaryTitleContainer} gap={3}>
                <Typography variant="subtitle9">{'Tier Summary'}</Typography>
              </GridContainer>
              <GridContainer mt={3} className={classes.accountSummaryTitleFields} gap={3}>
                <GridItem xs={4} lg={3.5}>
                  <Typography variant="subtitle4">Start Threshold</Typography>
                </GridItem>
                <GridItem xs={4} lg={3.5}>
                  <Typography variant="subtitle4">Tier Threshold</Typography>
                </GridItem>
                <GridItem xs={4} lg={3.5}>
                  <Typography variant="subtitle4">Tier Rate %</Typography>
                </GridItem>
                <GridItem xs={2} lg={2} />
              </GridContainer>

              <FieldArray name="tierSummaryState" validateOnChange={true}>
                {({ push, remove, form }) => {
                  const { values, touched, errors, handleChange } = form;

                  const tierSummaryState = values?.tierSummaryState?.length
                    ? values.tierSummaryState.map((row, index) => {
                        const previousTierThreshold =
                          index === 0 ? 0 : Number(values.tierSummaryState[index - 1]?.tierThreshold) || 0;

                        const calculatedStartBreakpoint = (previousTierThreshold + 0.01).toFixed(2);

                        // Update Formik's state dynamically
                        if (row.startBreakpoint !== calculatedStartBreakpoint) {
                          setFieldValue(`tierSummaryState[${index}].startBreakpoint`, calculatedStartBreakpoint);
                        }

                        return {
                          ...row,
                          startBreakpoint: calculatedStartBreakpoint, // Ensure calculated value is reflected
                        };
                      })
                    : [
                        { startBreakpoint: '0.01', tierThreshold: 0, tierRate: 1 },
                        { startBreakpoint: '0.01', tierThreshold: 0, tierRate: 1 },
                      ];
                  const canAddRow = tierSummaryState.length < 5;
                  const handleAddRow = () => {
                    // Get the Start Tier Breakpoint for the new row
                    const lastRow = tierSummaryState[tierSummaryState.length - 1];
                    const newStartBreakpoint = lastRow
                      ? parseInt(lastRow.tierThreshold || 0, 10) + 0.01 // If there's a previous row, take its Tier Breakpoint + 1
                      : 0.01; // Default to 0.01 for the first row

                    push({
                      startBreakpoint: newStartBreakpoint,
                      tierThreshold: '', // Leave Tier Breakpoint empty for user input
                      tierRate: '',
                    });
                  };

                  // Function to update "Start Breakpoint" of subsequent rows
                  const updateStartBreakpoints = (e, index) => {
                    for (let i = index + 1; i < tierSummaryState.length; i++) {
                      const previousTierBreakpoint = Number(e.target.value) || 0;
                      setFieldValue(
                        `tierSummaryState[${i}].startBreakpoint`,
                        (previousTierBreakpoint + 0.01).toFixed(2),
                      );
                    }
                  };
                  const onBlur = (e, fieldName) => {
                    let val = e.target.value;
                    // Allow empty or just decimal point
                    if (val === '' || val === '.') {
                      setFieldValue(fieldName, '');
                      return;
                    }
                    const decimalPlaces = fieldName.includes('tierThreshold') ? 4 : 2;
                    const zeroPadding = '0'.repeat(decimalPlaces);
                    if (!isNaN(val) && Number(val) === 0) {
                      val = `0.${zeroPadding}`;
                    } else if (val && !val.includes('.')) {
                      val = `${val}.${zeroPadding}`;
                    } else if (val && val.includes('.')) {
                      const [integer, decimal] = val.split('.');
                      const integerPart = integer || '0';
                      val = `${integerPart}.${(decimal + zeroPadding).slice(0, decimalPlaces)}`;
                    }
                    setFieldValue(fieldName, val);
                  };

                  return (
                    <>
                      {values?.tierSummaryState?.map((row, index) => (
                        <GridContainer p={0} mb={3} className={classes.accountSummaryFields} key={index} gap={3}>
                          <GridItem xs={4} lg={3.5} p={0}>
                            <TextField
                              name={`tierSummaryState[${index}].startBreakpoint`}
                              value={row.startBreakpoint || '0.01'}
                              placeholder="Start Tier Threshold"
                              disabled
                              onChange={handleChange}
                              error={Boolean(
                                touched?.tierSummaryState?.[index]?.startBreakpoint &&
                                  errors?.tierSummaryState?.[index]?.startBreakpoint,
                              )}
                              helperText={
                                touched?.tierSummaryState?.[index]?.startBreakpoint &&
                                errors?.tierSummaryState?.[index]?.startBreakpoint
                              }
                            />
                          </GridItem>

                          <GridItem xs={4} lg={3.5}>
                            <TextField
                              type="text"
                              name={`tierSummaryState[${index}].tierThreshold`}
                              placeholder="Tier Threshold"
                              value={row.tierThreshold || ''}
                              InputProps={{
                                'data-testid': 'rentDetailsForm',
                                startAdornment: (
                                  <InputAdornment position="start">{getCurrency(CURRENCY_CONSTANT.USD)}</InputAdornment>
                                ),
                              }}
                              onChange={(e) => {
                                let val = e.target.value.replace(/[^0-9.]/g, '');
                                const parts = val.split('.');
                                if (parts.length > 2) return;
                                if (parts[1] && parts[1].length > 4) return;
                                e.target.value = val;
                                handleChange(e);
                                updateStartBreakpoints(e, index);
                              }}
                              onBlur={(e) => onBlur(e, `tierSummaryState[${index}].tierThreshold`)}
                              error={Boolean(
                                touched?.tierSummaryState?.[index]?.tierThreshold &&
                                  errors?.tierSummaryState?.[index]?.tierThreshold,
                              )}
                              helperText={
                                touched?.tierSummaryState?.[index]?.tierThreshold &&
                                errors?.tierSummaryState?.[index]?.tierThreshold
                              }
                            />
                          </GridItem>

                          <GridItem xs={2} lg={2} className={classes.debitCreditField}>
                            <PercentTextField
                              label="Tier Rate"
                              useFormik={true}
                              allowDecimal={true}
                              decimalPlaces={4}
                              disableBlurFormat={true}
                              allowIntermediateDecimal={true}
                              name={`tierSummaryState[${index}].tierRate`}
                              value={row.tierRate || ''}
                              onChange={handleChange}
                              error={Boolean(
                                touched?.tierSummaryState?.[index]?.tierRate &&
                                  errors?.tierSummaryState?.[index]?.tierRate,
                              )}
                              helperText={
                                touched?.tierSummaryState?.[index]?.tierRate &&
                                errors?.tierSummaryState?.[index]?.tierRate
                              }
                            />
                          </GridItem>

                          {/* Hide delete button for the first row */}
                          {index > 1 && (
                            <GridItem xs={1} lg={1} sx={{ display: 'flex', alignItems: 'center' }}>
                              <IconButton onClick={() => remove(index)}>
                                <HighlightOff />
                              </IconButton>
                            </GridItem>
                          )}
                        </GridContainer>
                      ))}
                      <GridContainer pt={3}>
                        <GridItem xs={8} lg={6.7}>
                          <Box>
                            {/* Disable "Add New Row" after 5 rows */}
                            <Button
                              p={0}
                              className={classes.addNewRowBtn}
                              // onClick={() => canAddRow && push({ startBreakpoint: '', tierThreshold: '', tierRate: '' })}
                              onClick={handleAddRow}
                              disabled={!canAddRow}
                            >
                              Add New Row
                            </Button>
                          </Box>
                        </GridItem>
                      </GridContainer>
                    </>
                  );
                }}
              </FieldArray>
            </Box>
          )}
          <Box my={5} pb={5}>
            <Stack direction="row" data-testid="generalDetailsBtnWrapper">
              <LoadingButton
                loading={isSubmitting}
                variant="contained"
                data-testid="submitBtn"
                onClick={handleSubmit}
                disabled={isSubmitting || isLoading}
              >
                {isUpdate ? 'Update' : 'Add'}
              </LoadingButton>
              <Button onClick={onCancelClick} data-testid="backBtn" disabled={isSubmitting}>
                Cancel
              </Button>
            </Stack>
          </Box>
        </Box>
      </Form>
      {historyModal && (
        <Modal
          open={historyModal}
          onClose={handleCloseHistoryModal}
          title={`Lease History of ${values.leaseName || ''}`}
          titleVariant="subtitle10"
          closeIcon={true}
          PaperProps={{
            sx: {
              width: '90%',
              maxWidth: 'none',
              maxHeight: '90%',
              height: '70%',
              overflow: 'hidden',
            },
          }}
        >
          <LeaseHistory
            leaseId={leaseId}
            open={historyModal}
            onClose={handleCloseHistoryModal}
            entityId={leaseId}
            entityType="lease"
          />
        </Modal>
      )}
      <ConfirmationModal
        isOpen={subLocationsModal}
        title={
          <Stack direction="row" spacing={1}>
            <Box className={classes.errorIcon}>
              <ReportProblemIcon />
            </Box>
            <Box className={classes.errorText}>Error</Box>
          </Stack>
        }
        msg={`There is an active lease from ${getDateFormat(overlappingLeaseDates.termStartDate)} to
        ${getDateFormat(overlappingLeaseDates.termEndDate)} 
       for ${subLocationNames.length > 1 ? subLocationNames.join(' and ') : subLocationNames}.
       This overlaps with the new lease starting ${getDateFormat(formik.values.leaseBegins)}.
       Would you like to override the active lease by ending it on ${dayjs(formik.values.leaseBegins)
         .subtract(1, 'day')
         .format('MM/DD/YYYY')} so the new lease can start on ${getDateFormat(formik.values.leaseBegins)}?`}
        buttons={[
          { text: MESSAGE.YES, value: MESSAGE.YES },
          { text: MESSAGE.CANCEL, value: MESSAGE.CANCEL },
        ]}
        onClick={handleModalClick}
      />
    </FormikProvider>
  );
});
RentDetailsForm.displayName = displayName;
export default RentDetailsForm;
